import { FC, Fragment } from 'react';
import stringReplace from 'react-string-replace';
import { Box, Image, ListItem, UnorderedList } from '@chakra-ui/react';
import { RTAnyNode } from '@prismicio/types';
import { asTree } from 'prismic-richtext';

import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import ListIcon from 'components/ListIcon';
import logger from 'helpers/logger';

import { PrismicNode, PrismicTextNode } from './types';

type PrismicRichTextProps = {
  richText: RTAnyNode[];
};

const PRISMIC_CHECK_ICON = '✔️';

const replaceIconInText = (text: string) =>
  stringReplace(text, PRISMIC_CHECK_ICON, () => (
    <Box // better alignment with the text
      marginRight="space-4"
      position="relative"
      top="-1px"
    >
      <ListIcon color="lightBlue.0" name="tick" />
    </Box>
  ));

const formatText = (node: PrismicTextNode) => {
  switch (node.type) {
    case 'list-item':
      return (
        <ListItem key={node.key}>
          <Text>{node.element.text}</Text>
        </ListItem>
      );

    case 'hyperlink': {
      return (
        <a
          href={node.element.data.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Text color="ctaBlue.-1" fontWeight="weight-500" variant="primary">
            {node.text}
          </Text>
        </a>
      );
    }

    case 'em':
      return <Text fontStyle="italic">{replaceIconInText(node.text)}</Text>;

    case 'span':
      return <Text>{replaceIconInText(node.text)}</Text>;

    case 'strong':
      return (
        <Text fontWeight="weight-500">{replaceIconInText(node.text)}</Text>
      );

    default: {
      logger.warn('unsupported paragraph', node);
      return null;
    }
  }
};

const formatNode = (node: PrismicNode) => {
  switch (node.type) {
    case 'group-list-item':
      return (
        <UnorderedList>
          {node.children.map((listItem) => (
            <Fragment key={listItem.key}>{formatText(listItem)}</Fragment>
          ))}
        </UnorderedList>
      );

    case 'heading2':
      return (
        <Box paddingBottom="space-16" paddingTop="space-32">
          <Heading>{node.element.text}</Heading>
        </Box>
      );

    case 'heading3':
      return <Heading fontWeight="weight-400">{node.element.text}</Heading>;

    case 'paragraph':
      return (
        <Box>
          {node.children.map((paragrah) => (
            <Fragment key={paragrah.key}>{formatText(paragrah)}</Fragment>
          ))}
        </Box>
      );

    case 'image':
      return <Image maxWidth="100%" src={node.element.url} />;

    default: {
      logger.warn('unsupported node', node);
      return null;
    }
  }
};

const PrismicRichText: FC<PrismicRichTextProps> = ({ richText }) => {
  const { children } = asTree(richText);

  return (
    <>
      {children.map((node, i) => (
        <Fragment key={node.key}>
          {formatNode(node)}
          {i < children.length - 1 ? <Box height="space-16" /> : null}
        </Fragment>
      ))}
    </>
  );
};

export default PrismicRichText;
